// components/ItemsEditor/ItemsEditor.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { Card, Button, Table, Form, InputGroup, Badge, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ProductSelector from 'components/ProductSelector'
import { VarianteSelector } from 'components/VarianteSelector'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { API_UPLOADS_URL } from 'config/general'
import './ItemsEditor.css'

/**
 * Editor de items para pedidos
 * 
 * @param {array} items - Items originales del pedido
 * @param {function} onItemsChange - Callback cuando cambian los items
 * @param {boolean} disabled - Si está deshabilitado
 * @param {boolean} showPaymentWarning - Si mostrar advertencia de pago
 */
const ItemsEditor = ({
	items = [],
	onItemsChange = () => { },
	disabled = false,
	showPaymentWarning = false
}) => {
	const [itemsState, setItemsState] = useState([])
	const [selectedProduct, setSelectedProduct] = useState(null)
	const [newItemQuantity, setNewItemQuantity] = useState(1)

	// Estado para el modal de variantes
	const [showVarianteSelector, setShowVarianteSelector] = useState(false)
	const [productoParaVariante, setProductoParaVariante] = useState(null)

	// Inicializar estado
	useEffect(() => {
		if (items && items.length > 0) {
			const processedItems = items.map(item => ({
				...item,
				originalQuantity: parseInt(item.cantidad) || 0,
				currentQuantity: parseInt(item.cantidad) || 0,
				isOriginal: true,
				status: 'original', // original, modified, deleted, new
				atributosSeleccionados: item.atributosSeleccionados || null
			}))
			setItemsState(processedItems)
		} else {
			setItemsState([])
		}
	}, [items])

	// Función para formatear precio
	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	// Función para calcular subtotal de un item
	const calcularSubtotal = (item) => {
		const precio = item.precioPromocion || item.precio || item.monto || 0
		const cantidad = item.currentQuantity || 0
		return precio * cantidad
	}

	// Función para calcular total general
	const calcularTotalGeneral = useCallback(() => {
		return itemsState
			.filter(item => item.status !== 'deleted' && item.currentQuantity > 0)
			.reduce((total, item) => total + calcularSubtotal(item), 0)
	}, [itemsState])

	// Generar clave única para item (considera atributos)
	const generarClaveItem = (itemId, atributosSeleccionados) => {
		if (!atributosSeleccionados || atributosSeleccionados.length === 0) {
			return `item-${itemId}-sin-atributos`
		}
		const atributosHash = atributosSeleccionados
			.map(a => `${a.tipoId}:${a.valorId}`)
			.sort()
			.join('|')
		return `item-${itemId}-${atributosHash}`
	}

	// Función para procesar y enviar cambios al componente padre
	const updateChangesData = useCallback((updatedItems) => {
		const changes = {
			items: [], // Solo items COMPLETAMENTE NUEVOS
			eliminarItemsIds: [], // IDs de items eliminados
			modificarItems: [] // Items EXISTENTES con cantidad modificada
		}

		updatedItems.forEach(item => {
			if (item.status === 'new' && item.currentQuantity > 0) {
				// Items completamente nuevos
				changes.items.push({
					id: item.id,
					itemId: item.id,
					nombre: item.nombre,
					codigo: item.codigo,
					precio: item.precio,
					precioPromocion: item.precioPromocion,
					monto: item.monto || item.precioPromocion || item.precio,
					multimedia: item.multimedia,
					cantidad: item.currentQuantity,
					atributosSeleccionados: item.atributosSeleccionados || null
				})
			} else if (item.status === 'modified' && item.currentQuantity > 0) {
				// Items existentes modificados
				changes.modificarItems.push({
					id: item.id,
					cantidad: item.currentQuantity,
					monto: item.monto || item.precioPromocion || item.precio,
					nombre: item.nombre,
					pedidoItemId: item.pedidoItemId,
					atributosSeleccionados: item.atributosSeleccionados || null
				})
			} else if (item.status === 'deleted' && item.isOriginal) {
				changes.eliminarItemsIds.push(item.pedidoItemId || item.id)
			}
		})

		console.log('=== ItemsEditor enviando datos ===')
		console.log('Cambios completos:', changes)
		console.log('====================================')

		// Solo llamar onItemsChange si realmente hay cambios
		if (changes.items.length > 0 || changes.eliminarItemsIds.length > 0 || changes.modificarItems.length > 0) {
			onItemsChange(changes)
		} else {
			onItemsChange({ items: [], eliminarItemsIds: [], modificarItems: [] })
		}
	}, [onItemsChange])

	// Función para iniciar el proceso de agregar item
	const handleInitAddItem = () => {
		if (!selectedProduct) return

		// Guardar el producto y abrir el modal de variantes
		setProductoParaVariante(selectedProduct)
		setShowVarianteSelector(true)
	}

	// Función cuando se confirma la selección de variantes
	const handleVarianteConfirm = (producto, cantidad, atributosSeleccionados) => {
		agregarItemAlPedido(producto, cantidad, atributosSeleccionados)

		// Cerrar modal y limpiar
		setShowVarianteSelector(false)
		setProductoParaVariante(null)
		setSelectedProduct(null)
		setNewItemQuantity(1)
	}

	// Función cuando se cancela la selección de variantes
	const handleVarianteCancel = () => {
		setShowVarianteSelector(false)
		setProductoParaVariante(null)
	}

	// Función para agregar item al pedido
	const agregarItemAlPedido = (producto, cantidad, atributosSeleccionados) => {
		const claveNueva = generarClaveItem(producto.id, atributosSeleccionados)

		// Verificar si ya existe un item con el mismo producto Y mismos atributos
		const existingItemIndex = itemsState.findIndex(item => {
			const claveExistente = generarClaveItem(item.id, item.atributosSeleccionados)
			return claveExistente === claveNueva
		})

		if (existingItemIndex >= 0) {
			const existingItem = itemsState[existingItemIndex]

			if (existingItem.status === 'deleted') {
				// Si está eliminado, restaurarlo
				const updatedItems = [...itemsState]
				updatedItems[existingItemIndex].currentQuantity = cantidad
				updatedItems[existingItemIndex].status = existingItem.isOriginal ?
					(cantidad !== existingItem.originalQuantity ? 'modified' : 'original') : 'new'

				setItemsState(updatedItems)
				toast.success(`${producto.nombre} restaurado al pedido`)
			} else {
				// Si ya existe y no está eliminado, sumar cantidad
				const updatedItems = [...itemsState]
				updatedItems[existingItemIndex].currentQuantity += cantidad
				updatedItems[existingItemIndex].status = updatedItems[existingItemIndex].isOriginal ? 'modified' : 'new'

				setItemsState(updatedItems)
				toast.info(`Se sumó ${cantidad} unidad(es) a ${producto.nombre}`)
			}
		} else {
			// Agregar nuevo item
			const newItem = {
				id: producto.id,
				itemId: producto.id,
				nombre: producto.nombre,
				codigo: producto.codigo,
				precio: producto.precio,
				precioPromocion: producto.precioPromocion,
				monto: producto.precioPromocion || producto.precio,
				multimedia: producto.multimedia,
				originalQuantity: 0,
				currentQuantity: cantidad,
				isOriginal: false,
				status: 'new',
				atributosSeleccionados: atributosSeleccionados && atributosSeleccionados.length > 0
					? atributosSeleccionados
					: null,
				// Generar un ID único temporal para items nuevos con atributos
				tempId: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
			}

			setItemsState(prev => [...prev, newItem])

			// Mensaje de éxito con info de variantes
			if (atributosSeleccionados && atributosSeleccionados.length > 0) {
				const variantesStr = atributosSeleccionados.map(a => `${a.tipoNombre}: ${a.valorNombre}`).join(', ')
				toast.success(`${producto.nombre} (${variantesStr}) agregado al pedido`)
			} else {
				toast.success(`${producto.nombre} agregado al pedido`)
			}
		}
	}

	// Función para cambiar cantidad
	const handleQuantityChange = (itemIndex, newQuantity) => {
		const quantity = Math.max(0, parseInt(newQuantity) || 0)
		const updatedItems = [...itemsState]
		const item = updatedItems[itemIndex]

		item.currentQuantity = quantity

		// Determinar nuevo status
		if (!item.isOriginal) {
			// Item nuevo
			if (quantity === 0) {
				// Si es nuevo y va a 0, eliminarlo completamente
				updatedItems.splice(itemIndex, 1)
				setItemsState(updatedItems)
				updateChangesData(updatedItems)
				return
			} else {
				item.status = 'new'
			}
		} else {
			// Item original
			if (quantity === 0) {
				item.status = 'deleted'
			} else if (quantity !== item.originalQuantity) {
				item.status = 'modified'
			} else {
				item.status = 'original'
			}
		}

		setItemsState(updatedItems)
	}

	// Función para eliminar item completamente
	const handleRemoveItem = (itemIndex) => {
		const updatedItems = [...itemsState]
		const item = updatedItems[itemIndex]

		if (!item.isOriginal) {
			// Si es nuevo, eliminarlo de la lista
			updatedItems.splice(itemIndex, 1)
		} else {
			// Si es original, marcarlo como eliminado
			item.status = 'deleted'
			item.currentQuantity = 0
		}

		setItemsState(updatedItems)
	}

	// Función para restaurar item eliminado
	const handleRestoreItem = (itemIndex) => {
		const updatedItems = [...itemsState]
		const item = updatedItems[itemIndex]

		// Restaurar con la cantidad original o 1 si era nuevo
		const cantidadRestaurada = item.originalQuantity > 0 ? item.originalQuantity : 1
		item.currentQuantity = cantidadRestaurada

		// Determinar nuevo status
		if (item.isOriginal) {
			item.status = cantidadRestaurada !== item.originalQuantity ? 'modified' : 'original'
		} else {
			item.status = 'new'
		}

		setItemsState(updatedItems)
		toast.success(`${item.nombre} restaurado al pedido`)
	}

	// Effect para notificar cambios solo cuando sea necesario
	useEffect(() => {
		// Solo actualizar si hay items con cambios reales
		const hasChanges = itemsState.some(item =>
			item.status !== 'original' && (item.status !== 'new' || item.currentQuantity > 0)
		)

		// Siempre notificar al padre sobre el estado actual
		if (itemsState.length > 0 || hasChanges) {
			updateChangesData(itemsState)
		}
	}, [itemsState, updateChangesData])

	// Función para obtener clase CSS según status
	const getRowClassName = (item) => {
		switch (item.status) {
			case 'new': return 'table-success'
			case 'deleted': return 'table-danger'
			case 'modified': return 'table-warning'
			default: return ''
		}
	}

	// Función para obtener badge de status
	const getStatusBadge = (item) => {
		switch (item.status) {
			case 'new':
				return <Badge bg="success" className="ms-2">Nuevo</Badge>
			case 'deleted':
				return <Badge bg="danger" className="ms-2">Eliminado</Badge>
			case 'modified':
				return <Badge bg="warning" text="dark" className="ms-2">Modificado</Badge>
			default:
				return null
		}
	}

	// Renderizar badges de atributos
	const renderAtributosBadges = (atributosSeleccionados) => {
		if (!atributosSeleccionados || atributosSeleccionados.length === 0) {
			return null
		}

		return (
			<div className="atributos-badges mt-1">
				{atributosSeleccionados.map((attr, idx) => (
					<Badge
						key={idx}
						bg="info"
						className="me-1 mb-1"
						style={{ fontSize: '0.75rem' }}
					>
						{attr.tipoNombre}: {attr.valorNombre}
					</Badge>
				))}
			</div>
		)
	}

	return (
		<Card className="shadow-sm">
			<Card.Header className="bg-light">
				<h6 className="mb-0">
					<i className="fa fa-shopping-cart me-2"></i>
					Items del Pedido ({itemsState.filter(i => i.status !== 'deleted' && i.currentQuantity > 0).length})
				</h6>
			</Card.Header>
			<Card.Body>
				{showPaymentWarning && (
					<Alert variant="warning" className="mb-3">
						<i className="fa fa-exclamation-triangle me-2"></i>
						<strong>Pedido pagado:</strong> Al cambiar los items, probablemente cambie el monto total a pagar.
						¿Está seguro que desea continuar? Podrá gestionar el pago/devolución por fuera de la plataforma.
					</Alert>
				)}

				{/* Selector para agregar nuevos productos */}
				<div className="mb-4 p-3 bg-light rounded">
					<h6>Agregar Producto</h6>
					<div className="d-flex gap-2 align-items-end">
						<div className="flex-grow-1">
							<Form.Label>Producto</Form.Label>
							<ProductSelector
								value={selectedProduct}
								onChange={setSelectedProduct}
								placeholder="Buscar producto para agregar..."
								disabled={disabled}
							/>
						</div>
						<div style={{ width: '100px' }}>
							<Form.Label>Cantidad</Form.Label>
							<Form.Control
								type="number"
								min="1"
								value={newItemQuantity}
								onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
								disabled={disabled}
							/>
						</div>
						<Button
							variant="success"
							onClick={handleInitAddItem}
							disabled={disabled || !selectedProduct}
						>
							<i className="fa fa-plus me-1"></i>
							Agregar
						</Button>
					</div>
					<small className="text-muted mt-2 d-block">
						<i className="fa fa-info-circle me-1"></i>
						Si el producto tiene variantes, se abrirá un selector para elegirlas.
					</small>
				</div>

				{/* Lista de items */}
				{itemsState.length > 0 ? (
					<>
						<Table striped bordered hover responsive>
							<thead>
								<tr>
									<th style={{ width: '60px' }}>Imagen</th>
									<th>Producto</th>
									<th style={{ width: '120px' }}>Precio Unit.</th>
									<th style={{ width: '120px' }}>Cantidad</th>
									<th style={{ width: '120px' }}>Subtotal</th>
									<th style={{ width: '80px' }}>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{itemsState.map((item, index) => (
									<tr key={item.tempId || item.pedidoItemId || `${item.id}-${index}`} className={getRowClassName(item)}>
										<td className="text-center">
											{item.multimedia && item.multimedia.length > 0 ? (
												<img
													src={API_UPLOADS_URL + item.multimedia[0].url}
													alt={item.nombre}
													className="item-image"
												/>
											) : (
												<div className="item-image-placeholder">
													<i className="fa fa-image text-muted"></i>
												</div>
											)}
										</td>
										<td>
											<div className="d-flex flex-column">
												<div className="d-flex align-items-center">
													<span className="fw-bold">{item.nombre}</span>
													{getStatusBadge(item)}
												</div>
												{item.codigo && (
													<small className="text-muted">Código: {item.codigo}</small>
												)}
												{/* Mostrar atributos seleccionados */}
												{renderAtributosBadges(item.atributosSeleccionados)}
											</div>
										</td>
										<td className="text-end">
											{item.precioPromocion ? (
												<div>
													<small className="text-decoration-line-through text-muted">
														{formatearPrecio(item.precio)}
													</small>
													<div className="text-danger fw-bold">
														{formatearPrecio(item.precioPromocion)}
													</div>
												</div>
											) : (
												formatearPrecio(item.monto || item.precio)
											)}
										</td>
										<td>
											{item.status === 'deleted' ? (
												<div className="text-center">
													<Badge bg="danger">Eliminado</Badge>
													<div className="mt-1">
														<Button
															size="sm"
															variant="outline-success"
															onClick={() => handleRestoreItem(index)}
															disabled={disabled}
															title="Restaurar item"
														>
															<i className="fa fa-undo me-1"></i>
															Restaurar
														</Button>
													</div>
												</div>
											) : (
												<InputGroup size="sm">
													<Button
														variant="outline-secondary"
														onClick={() => handleQuantityChange(index, item.currentQuantity - 1)}
														disabled={disabled || item.currentQuantity <= 0}
													>
														<i className="fa fa-minus"></i>
													</Button>
													<Form.Control
														type="number"
														min="0"
														value={item.currentQuantity}
														onChange={(e) => handleQuantityChange(index, e.target.value)}
														disabled={disabled}
														className="text-center quantity-input-no-arrows"
													/>
													<Button
														variant="outline-secondary"
														onClick={() => handleQuantityChange(index, item.currentQuantity + 1)}
														disabled={disabled}
													>
														<i className="fa fa-plus"></i>
													</Button>
												</InputGroup>
											)}
										</td>
										<td className="text-end fw-bold text-primary">
											{item.status === 'deleted' ?
												<span className="text-muted">₲ 0</span> :
												formatearPrecio(calcularSubtotal(item))
											}
										</td>
										<td className="text-center">
											{item.status === 'deleted' ? (
												<Button
													variant="outline-success"
													size="sm"
													onClick={() => handleRestoreItem(index)}
													disabled={disabled}
													title="Restaurar item"
												>
													<i className="fa fa-undo"></i>
												</Button>
											) : (
												<Button
													variant="outline-danger"
													size="sm"
													onClick={() => handleRemoveItem(index)}
													disabled={disabled}
													title="Eliminar item"
												>
													<i className="fa fa-trash"></i>
												</Button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</Table>

						{/* Total */}
						<div className="text-end mt-3">
							<h5 className="text-primary">
								<strong>Total: {formatearPrecio(calcularTotalGeneral())}</strong>
							</h5>
						</div>

						{/* Leyenda */}
						<div className="mt-3 p-2 bg-light rounded">
							<small>
								<strong>Leyenda:</strong>
								<Badge bg="success" className="ms-2">Nuevo</Badge> - Items agregados al pedido
								<Badge bg="warning" text="dark" className="ms-2">Modificado</Badge> - Cantidad cambiada
								<Badge bg="danger" className="ms-2">Eliminado</Badge> - Items eliminados del pedido
								<Badge bg="info" className="ms-2">Talla: M</Badge> - Variante seleccionada
							</small>
						</div>
					</>
				) : (
					<div className="text-center py-4">
						<i className="fa fa-shopping-cart fa-3x text-muted mb-3"></i>
						<p className="text-muted mb-0">No hay items en este pedido</p>
						<small className="text-muted">Agregue productos usando el selector de arriba</small>
					</div>
				)}
			</Card.Body>

			{/* Modal de selección de variantes */}
			<VarianteSelector
				show={showVarianteSelector}
				producto={productoParaVariante}
				onConfirm={handleVarianteConfirm}
				onCancel={handleVarianteCancel}
				cantidadInicial={newItemQuantity}
			/>
		</Card>
	)
}

export default ItemsEditor